import React, {useEffect, useRef, useState} from 'react'
import {useSearchParams, Link} from 'react-router-dom'
import axios from 'axios'
import SignatureCanvas from 'react-signature-canvas'

const API_URL = process.env.REACT_APP_API_URL

interface CheckInFormData {
  appointmentId: number
  recordId?: string | null
  appointmentDate: string
  startTime: string
  clientName: string
  location: string
  interpreterName: string
  staffName: string
  checkInAt: string
  isApproved?: boolean
  approvedAt?: string
}

type PageState = 'loading' | 'form' | 'success' | 'error' | 'already-approved'

export const CheckInApprovalPage: React.FC = () => {
  const [search] = useSearchParams()
  const appointmentId = search.get('appointmentId') ?? ''
  const token = search.get('token') ?? ''

  const sigRef = useRef<SignatureCanvas>(null)
  const sigContainerRef = useRef<HTMLDivElement>(null)
  const [state, setState] = useState<PageState>('loading')
  const [formData, setFormData] = useState<CheckInFormData | null>(null)
  const [approvedAt, setApprovedAt] = useState<string | null>(null)
  const [errorMsg, setErrorMsg] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [signatureError, setSignatureError] = useState('')

  const resizeSignatureCanvas = () => {
    const pad = sigRef.current as SignatureCanvas & {_resizeCanvas?: () => void} | null
    pad?._resizeCanvas?.()
  }

  useEffect(() => {
    if (state !== 'form') return

    const resize = () => resizeSignatureCanvas()
    const timer = window.setTimeout(resize, 100)

    const container = sigContainerRef.current
    const observer = container ? new ResizeObserver(() => resize()) : null
    if (container && observer) {
      observer.observe(container)
    }
    window.addEventListener('resize', resize)

    return () => {
      window.clearTimeout(timer)
      observer?.disconnect()
      window.removeEventListener('resize', resize)
    }
  }, [state])

  useEffect(() => {
    if (!appointmentId || !token) {
      setErrorMsg('Missing appointment ID or token in the URL.')
      setState('error')
      return
    }
    loadFormData()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const loadFormData = async () => {
    try {
      const url = `${API_URL}/Appoinment/${appointmentId}/check-in-form?token=${encodeURIComponent(token)}`
      const res = await axios.get(url)
      const data = res.data as CheckInFormData & {
        CheckInAt?: string
        ApprovedAt?: string
        RecordId?: string | null
      }
      setFormData({
        ...data,
        recordId: data.recordId ?? data.RecordId ?? null,
        checkInAt: data.checkInAt ?? data.CheckInAt ?? '',
        approvedAt: data.approvedAt ?? data.ApprovedAt,
      })

      if (data.isApproved) {
        setApprovedAt(data.approvedAt ?? data.ApprovedAt ?? data.checkInAt ?? data.CheckInAt ?? null)
        setState('already-approved')
        return
      }

      setState('form')
    } catch (err: any) {
      const msg =
        err?.response?.data?.text || err?.response?.data?.Text || 'Invalid or expired link.'
      setErrorMsg(msg)
      setState('error')
    }
  }

  const handleSubmit = async () => {
    const sigInstance: any = sigRef.current as any
    if (!sigInstance || (typeof sigInstance.isEmpty === 'function' && sigInstance.isEmpty())) {
      setSignatureError('Signature is required.')
      return
    }
    setSignatureError('')

    const trimmed = sigInstance.getTrimmedCanvas()
    const dataUrl = trimmed ? trimmed.toDataURL('image/png') : null
    if (!dataUrl) {
      setSignatureError('Could not capture signature.')
      return
    }

    setSubmitting(true)
    try {
      const res = await axios.post(`${API_URL}/Appoinment/${appointmentId}/check-in-approval`, {
        token,
        signature: dataUrl,
      })
      const responseText =
        res?.data?.text || res?.data?.Text || ''
      if (responseText.toLowerCase().includes('already')) {
        setApprovedAt(res?.data?.checkedInAt ?? null)
        setState('already-approved')
      } else {
        setState('success')
      }
    } catch (err: any) {
      const msg = err?.response?.data?.text || err?.response?.data?.Text || 'Failed to approve.'
      if (msg.toLowerCase().includes('already')) {
        setState('already-approved')
      } else {
        setErrorMsg(msg)
        setState('error')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className='min-vh-100 d-flex flex-column'
      style={{backgroundColor: 'var(--kt-body-bg, #f5f5f5)'}}
    >
      <header className='bg-white shadow-sm py-3'>
        <div className='container d-flex align-items-center'>
          <Link to='/dashboard'>
            <img
              alt='Logo'
              src={'/media/logos/Ad _Astra_Logo_Red_prod.png'}
              className='h-35px'
            />
          </Link>
        </div>
      </header>

      <main className='flex-grow-1 d-flex align-items-center justify-content-center py-5'>
        <div className='container'>
          <div className='row justify-content-center'>
            <div className='col-12 col-lg-8 col-xl-6'>
              {state === 'loading' && (
                <div className='card shadow-sm'>
                  <div className='card-body text-center py-10'>
                    <div className='spinner-border text-primary' role='status' />
                    <p className='mt-3 text-muted'>Loading check-in details...</p>
                  </div>
                </div>
              )}

              {state === 'form' && formData && (
                <div className='card shadow-sm'>
                  <div className='card-header bg-white border-bottom'>
                    <h3 className='card-title fw-bold mb-0'>Check-In Approval</h3>
                  </div>
                  <div className='card-body'>
                    <p className='text-muted mb-4'>
                      Please review the check-in details and sign below to approve.
                    </p>

                    <div className='mb-4'>
                      <div className='row mb-2'>
                        <div className='col-5 fw-semibold text-gray-600'>Appointment ID</div>
                        <div className='col-7'>{formData.appointmentId || 'N/A'}</div>
                      </div>
                      <div className='row mb-2'>
                        <div className='col-5 fw-semibold text-gray-600'>Record ID</div>
                        <div className='col-7'>{formData.recordId?.trim() || 'N/A'}</div>
                      </div>
                      <div className='row mb-2'>
                        <div className='col-5 fw-semibold text-gray-600'>Client</div>
                        <div className='col-7'>{formData.clientName || 'N/A'}</div>
                      </div>
                      <div className='row mb-2'>
                        <div className='col-5 fw-semibold text-gray-600'>Appointment Date</div>
                        <div className='col-7'>{formData.appointmentDate || 'N/A'}</div>
                      </div>
                      <div className='row mb-2'>
                        <div className='col-5 fw-semibold text-gray-600'>Start Time</div>
                        <div className='col-7'>{formData.startTime || 'N/A'}</div>
                      </div>
                      <div className='row mb-2'>
                        <div className='col-5 fw-semibold text-gray-600'>Location</div>
                        <div className='col-7'>{formData.location || 'N/A'}</div>
                      </div>
                      <div className='row mb-2'>
                        <div className='col-5 fw-semibold text-gray-600'>Interpreter</div>
                        <div className='col-7'>{formData.interpreterName || 'N/A'}</div>
                      </div>
                      <div className='row mb-2'>
                        <div className='col-5 fw-semibold text-gray-600'>Staff Member</div>
                        <div className='col-7'>{formData.staffName || 'N/A'}</div>
                      </div>
                      <div className='row mb-2'>
                        <div className='col-5 fw-semibold text-gray-600'>Checked In At</div>
                        <div className='col-7'>
                          {formData.checkInAt
                            ? new Date(formData.checkInAt).toLocaleString()
                            : 'N/A'}
                        </div>
                      </div>
                    </div>

                    <div className='mb-3'>
                      <label className='form-label fw-semibold'>Your Signature</label>
                      <div
                        ref={sigContainerRef}
                        style={{
                          border: signatureError ? '2px solid #dc3545' : '1px solid #dee2e6',
                          borderRadius: '0.375rem',
                          backgroundColor: '#fff',
                          width: '100%',
                          height: 150,
                        }}
                      >
                        <SignatureCanvas
                          ref={sigRef}
                          canvasProps={{
                            className: 'w-100 h-100',
                            style: {width: '100%', height: '100%', touchAction: 'none'},
                          }}
                          backgroundColor='rgb(255,255,255)'
                          penColor='black'
                        />
                      </div>
                      {signatureError && (
                        <div className='text-danger small mt-1'>{signatureError}</div>
                      )}
                      <button
                        type='button'
                        className='btn btn-sm btn-light mt-1'
                        onClick={() => {
                          sigRef.current?.clear()
                          setSignatureError('')
                        }}
                      >
                        Clear signature
                      </button>
                    </div>

                    <button
                      type='button'
                      className='btn btn-primary w-100'
                      onClick={handleSubmit}
                      disabled={submitting}
                    >
                      {submitting ? (
                        <>
                          <span className='spinner-border spinner-border-sm me-2' />
                          Submitting...
                        </>
                      ) : (
                        'Approve Check-In'
                      )}
                    </button>
                  </div>
                </div>
              )}

              {state === 'success' && (
                <div className='card shadow-sm'>
                  <div className='card-body text-center py-10'>
                    <div className='mb-4'>
                      <i
                        className='bi bi-check-circle-fill text-success'
                        style={{fontSize: '4rem'}}
                      />
                    </div>
                    <h4 className='fw-bold mb-2'>Check-In Approved</h4>
                    <p className='text-muted'>
                      The interpreter has been notified. You may close this page.
                    </p>
                  </div>
                </div>
              )}

              {state === 'already-approved' && (
                <div className='card shadow-sm'>
                  <div className='card-body text-center py-10'>
                    <div className='mb-4'>
                      <i
                        className='bi bi-info-circle-fill text-info'
                        style={{fontSize: '4rem'}}
                      />
                    </div>
                    <h4 className='fw-bold mb-2'>Check-In Approved</h4>
                    <p className='text-muted mb-0'>
                      This check-in has already been approved. No further action is needed.
                    </p>
                    {approvedAt && (
                      <p className='text-muted small mt-2 mb-0'>
                        Approved on {approvedAt}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {state === 'error' && (
                <div className='card shadow-sm'>
                  <div className='card-body text-center py-10'>
                    <div className='mb-4'>
                      <i
                        className='bi bi-exclamation-triangle-fill text-danger'
                        style={{fontSize: '4rem'}}
                      />
                    </div>
                    <h4 className='fw-bold mb-2'>Error</h4>
                    <p className='text-muted'>{errorMsg || 'Something went wrong.'}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <footer className='py-3 text-center text-gray-500 small'>
        <div className='container'>
          Check-in approval — sign to confirm the interpreter's arrival.
        </div>
      </footer>
    </div>
  )
}

export default CheckInApprovalPage
